export enum PayoutMethodType {
  VEST = 'vest_internal',
  PAYPAL = 'paypal',
  BANK = 'bank_wire',
  CRYPTO = 'crypto',
}

export enum AffiliateTier {
  BASIC = 'basic',
  VIP = 'vip',
  PARTNERSHIP = 'partnership',
}

export function isAffiliateDashboardTier(
  tier: AffiliateTier | undefined | null,
) {
  return tier === AffiliateTier.VIP || tier === AffiliateTier.PARTNERSHIP;
}

export enum CommissionState {
  DUE = 'due',
  PAID = 'paid',
  PENDING = 'pending',
  REVERSED = 'reversed',
}

export enum AffiliateStatus {
  ACTIVE = 'active',
}

export enum ReferralStatus {
  PROVISIONAL = 'provisional',
  PERMANENT = 'permanent',
  SUPERSEDED = 'superseded',
  EXPIRED = 'expired',
  DEACTIVATED = 'deactivated',
}

export enum AffiliateSource {
  SELF_APPLY = 'self_apply',
  INVITE_LINK = 'invite_link',
}

export enum ApplicationStatus {
  SUBMITTED = 'submitted',
  APPROVED = 'approved',
}

export interface LinkResponse {
  id: string;
  token: string;
  normalized_token: string;
  is_default: boolean;
  status: AffiliateStatus;
}

export interface CouponResponse {
  id: string;
  code: string;
  normalized_code: string;
  is_default: boolean;
  status: AffiliateStatus;
  discount_percent: number;
  final_price_cents?: number;
  currency?: string;
  commission_percent?: number;
  stripe_promotion_code_id?: string;
}

export interface CheckoutSuggestionCouponResponse {
  code: string;
  currency: string;
  discount_percent: number;
  final_price_cents: number;
  human_label: string;
  stripe_promotion_code_id: string;
}

export interface CheckoutSuggestionResponse {
  affiliate_id: string;
  attribution_kind: string;
  default_coupon: CheckoutSuggestionCouponResponse | null;
}

export interface ApplicationResponse {
  id: string;
  source: AffiliateSource;
  stored_status: ApplicationStatus;
  status: ApplicationStatus;
  account_id: string;
  email: string;
  contact_email: string;
  display_name: string;
  affiliate_id: string;
  decided_at_ms: number;
  created_at_ms: number;
  updated_at_ms: number;
  terms: {
    tier: AffiliateTier;
    default_discount_percent: number;
    default_commission_percent: number;
  };
}

interface ProfileResponse {
  affiliate_id: string;
  account_id: string;
  display_name: string;
  status: 'approved';
  tier: AffiliateTier;
  default_discount_percent: number;
  default_commission_percent: number;
}

export interface StatusResponse {
  is_affiliate: boolean;
  account_id?: string;
  status?: 'approved' | 'archived' | 'disabled' | 'not_started' | 'pending';
  profile?: ProfileResponse;
  application?: ApplicationResponse;
  default_link?: LinkResponse;
  default_coupon?: CouponResponse;
  default_assets_state?:
    | 'ready'
    | 'link_missing'
    | 'coupon_missing'
    | 'coupon_needs_setup'
    | 'provisioning_failed';
}

export interface DashboardResponse {
  links: LinkResponse[];
  coupons: CouponResponse[];
  commission_summary: {
    due_cents: number;
    pending_cents: number;
    paid_cents: number;
    reversed_cents: number;
    net_cents: number;
    // Subset of due_cents still inside the refund-hold window — earned but
    // not yet payable.
    held_cents: number;
    currency: string;
  };
  referral_summary: {
    total: number;
    provisional: number;
    permanent: number;
  };
  payout_summary: {
    due_cents: number;
    paid_cents: number;
    currency: string;
    available_payout_cents: number;
    can_request_payout: boolean;
  };
}

export interface CryptoPayoutOptionResponse {
  asset: string;
  network: string;
}

export interface PayoutMethodResponse {
  id: string;
  affiliate_id: string;
  method_type: PayoutMethodType;
  status: AffiliateStatus;
  created_at_ms: number;
  updated_at_ms: number;
  bank_wire?: {
    account_holder_name: string;
    account_number_masked: string;
    bank_name: string;
    country: string;
    routing_number_masked: string;
    swift_masked: string;
  };
  paypal?: {
    email_masked: string;
  };
  crypto?: CryptoPayoutOptionResponse & {
    wallet_address?: string;
  };
}

export interface PayoutRequestResponse {
  amount_cents: number;
  currency: string;
  id: string;
  method_type: PayoutMethodType;
  notes: string;
  processed_at_ms: number;
  rejected_reason: string;
  requested_at_ms: number;
  status: string; // TODO
}

export interface AffiliateReferralResponse {
  became_permanent_at_ms: number;
  created_at_ms: number;
  expires_at_ms: number;
  id: string;
  referee_account_id: string;
  source_code: string;
  source_code_id: string;
  source_coupon_id: string;
  source_label: string;
  source_link_id: string;
  source_type: string;
  status: ReferralStatus;
}

export interface CommissionResponse {
  id: string;
  amount_cents: number;
  created_at_ms: number;
  currency: string;
  due_at_ms: number;
  paid_at_ms: number;
  payout_batch_id: string;
  source_ref: string;
  source_type: string;
  state: string;
}

export interface CommissionsAnalyticsResponse {
  daily: Array<{
    date: string;
    due_cents: number;
    net_cents: number;
    paid_cents: number;
    pending_cents: number;
    reversed_cents: number;
  }>;
  totals: {
    currency: string;
    due_cents: number;
    net_cents: number;
    paid_cents: number;
    pending_cents: number;
    reversed_cents: number;
  };
}
